import { IMetodoDePagamentoJson } from "../models/metodoDePagamento";
import { IImpostoJson } from "../models/imposto";
import { IPosicaoJson } from "../models/posicao";
import { IPosicaoInternacionalJson } from "../models/posicaoInternacional";
import { IImpostoInternacionalJson } from "../models/impostoInternacional";
import { IPatrimonioJson } from "../models/analises/patrimonio";
import {
  IRentabilidadeBovespaJson,
  IRentabilidadeJson,
} from "../models/analises/rentabilidade";
import { IDistribuicaoJson } from "../models/analises/distribuicao";
import { IDireitoJson } from "../models/direito";
import { ISaldoBrutoJson } from "../models/analises/saldoBruto";
import { getFunctions, httpsCallable } from "firebase/functions";
import { IProdutoJson } from "../models/analises/produto";
import {
  Perfil,
  Role,
  TipoDocumento,
  TipoEventoInternacional,
  MoedaInternacional,
} from "../models/enum";
import Ativo from "../models/ativo";
import { ICotacaoJson } from "../models/analises/cotacao";
import Permissoes from "../models/permissoes";
import { IPosicaoAtivoJson } from "../models/analises/posicaoAtivo";
import { User } from "firebase/auth";
import Usuario from "../models/usuario";
import Business from "../models/business";

export default class Functions {
  //#region assinatura
  obterMetodoDePagamento = httpsCallable<void, IMetodoDePagamentoJson[]>(
    getFunctions(),
    "assinatura-obterMetodoDePagamento"
  );

  atualizarInfoFiscal = httpsCallable<
    { tipoDocumento: TipoDocumento; documento: string },
    void
  >(getFunctions(), "assinatura-atualizarInfoFiscal");

  obterLinkPainelDoCliente = httpsCallable<
    { returnUrl: string },
    {
      id: string;
      url: string;
    }
  >(getFunctions(), "ext-firestore-stripe-subscriptions-createPortalLink");

  escolherPerfil = httpsCallable<{ perfil: Perfil }, void>(
    getFunctions(),
    "usuario-escolherPerfil"
  );

  obterTabelaDePrecos = httpsCallable<
    void,
    { customerSessionClientSecret: string }
  >(getFunctions(), "assinatura-obterTabelaDePrecos");
  //#endregion

  //#region front
  ativoAutoComplete = httpsCallable<{ codigoAtivo: string }, { ativo?: Ativo }>(
    getFunctions(),
    "front-ativoAutoComplete"
  );

  direitoAutoComplete = httpsCallable<
    { codigoAtivo: string; data: string },
    { direito?: IDireitoJson }
  >(getFunctions(), "front-direitoAutoComplete");

  relerUpload = httpsCallable<{ uploadId: string }, void>(
    getFunctions(),
    "notas-relerUpload"
  );

  definirSenhaPdf = httpsCallable<
    { uploadId: string; senha: string },
    { jaCadastrada: boolean }
  >(getFunctions(), "notas-definirSenhaPdf");

  prepararNotaManual = httpsCallable<{ notaId: string }, void>(
    getFunctions(),
    "notas-prepararNotaManual"
  );
  //#endregion

  //#region calculos
  declarar = httpsCallable<{ ano: number }, { ano: number; usuario: string }>(
    getFunctions(),
    "calculos-relatoriosDIRPF",
    { timeout: 540 * 1000 }
  );

  pontoDePartida = httpsCallable<{ pontoDePartida: IImpostoJson }, void>(
    getFunctions(),
    "calculos-pontoDePartida"
  );

  posicaoInicial = httpsCallable<{ posicaoInicial: IPosicaoJson }, void>(
    getFunctions(),
    "calculos-posicaoInicial"
  );

  calcular = httpsCallable<{ dataInicio: string; dataFim: string }, void>(
    getFunctions(),
    "calculos-calcular",
    {
      timeout: 540 * 1000,
    }
  );

  alterarPontoDePartida = httpsCallable<{ data: string }, void>(
    getFunctions(),
    "calculos-alterarPontoDePartida"
  );

  posicaoInicialInternacional = httpsCallable<
    { posicaoInicial: IPosicaoInternacionalJson },
    void
  >(getFunctions(), "calculos-posicaoInicialInternacional");

  pontoDePartidaInternacional = httpsCallable<
    { pontoDePartida: IImpostoInternacionalJson },
    void
  >(getFunctions(), "calculos-pontoDePartidaInternacional");

  alterarPontoDePartidaInternacional = httpsCallable<{ data: string }, void>(
    getFunctions(),
    "calculos-alterarPontoDePartidaInternacional"
  );
  //#endregion

  //#region internacional (eventos corporativos manuais)
  registrarEventoInternacional = httpsCallable<
    {
      ativo: string;
      data: string;
      evento: TipoEventoInternacional;
      // Proporção: só em desdobramento/grupamento
      de?: number;
      para?: number;
      moeda: MoedaInternacional;
      // Só em troca de ticker
      codigoNovo?: string;
    },
    { id: string }
  >(getFunctions(), "internacional-registrarEventoInternacional");
  excluirEventoInternacional = httpsCallable<{ operacaoId: string }, void>(
    getFunctions(),
    "internacional-excluirEventoInternacional"
  );
  // PTAX de cada moeda na data (ou no último dia útil antes dela)
  obterCambioDoDia = httpsCallable<
    { data: string; moedas: MoedaInternacional[] },
    Partial<
      Record<
        MoedaInternacional,
        { compra: number; venda: number; dataCotacao: string }
      >
    >
  >(getFunctions(), "internacional-obterCambioDoDia");
  //#endregion

  //#region dw
  obterPatrimonio = httpsCallable<void, IPatrimonioJson[]>(
    getFunctions(),
    "dw-patrimonio"
  );

  obterPatrimonioSnapshot = httpsCallable<{ data: string }, IPatrimonioJson[]>(
    getFunctions(),
    "dw-patrimonioSnapshot"
  );

  obterRentabilidade = httpsCallable<
    void,
    {
      carteira: IRentabilidadeJson[];
      ibov: IRentabilidadeBovespaJson[];
    }
  >(getFunctions(), "dw-rentabilidade");

  obterDistribuicao = httpsCallable<void, IDistribuicaoJson[]>(
    getFunctions(),
    "dw-distribuicao"
  );

  obterSaldoBruto = httpsCallable<{ data: string }, ISaldoBrutoJson[]>(
    getFunctions(),
    "dw-saldoBruto"
  );

  obterProdutos = httpsCallable<void, IProdutoJson[]>(
    getFunctions(),
    "dw-produtos"
  );

  obterCotacao = httpsCallable<{ codigoAtivo: string }, ICotacaoJson[]>(
    getFunctions(),
    "dw-cotacaoAtivo"
  );

  obterPosicaoAtivo = httpsCallable<
    { codigoAtivo: string },
    IPosicaoAtivoJson[]
  >(getFunctions(), "dw-posicaoAtivo");
  //#endregion

  //#region b2b
  enviarConviteCliente = httpsCallable<
    { id: string; nome: string; email: string },
    void
  >(getFunctions(), "b2b-enviarConviteCliente");

  editarCliente = httpsCallable<
    { id: string; nome: string; email: string },
    void
  >(getFunctions(), "b2b-editarCliente");

  enviarConviteUsuario = httpsCallable<{ nome: string; email: string }, void>(
    getFunctions(),
    "b2b-enviarConviteUsuario"
  );

  atualizarPermissoes = httpsCallable<
    { target: string; permissoes: Permissoes; perfil: Perfil },
    void
  >(getFunctions(), "b2b-atualizarPermissoes");

  obterUsuarioAuth = httpsCallable<
    { target: string },
    {
      targetAuth: {
        uid: string;
        email: string;
        emailVerified: boolean;
        displayName: string;
        photoURL: string;
        disabled: boolean;
        metadata: {
          lastSignInTime: Date;
          creationTime: Date;
        };
        customClaims?: {
          perfil: Perfil;
          permissoes: Permissoes;
          stripeRole: Role;
        };
      };
    }
  >(getFunctions(), "b2b-obterUsuarioAuth");

  excluirUsuario = httpsCallable<
    {
      colaboradorId: string;
    },
    void
  >(getFunctions(), "b2b-excluirUsuario");

  arquivarCliente = httpsCallable<
    {
      clienteId: string;
    },
    void
  >(getFunctions(), "b2b-arquivarCliente");

  desarquivarCliente = httpsCallable<
    {
      clienteId: string;
    },
    void
  >(getFunctions(), "b2b-desarquivarCliente");

  desvincularCliente = httpsCallable<
    {
      clienteId: string;
    },
    void
  >(getFunctions(), "b2b-desvincularCliente");

  criarContador = httpsCallable<
    {
      contador: {
        nome: string;
        email: string;
        imagemUrl?: string;
        origem?: string;
      };
      business: {
        acao: "criar";
        nome: string;
        imagemUrl?: string;
      };
    },
    {
      usuarioAuth: User;
      usuario: Usuario;
      business: Business;
      clientes: Usuario[];
      clienteCriado: Usuario;
    }
  >(getFunctions(), "b2b-criarContador");
  //#endregion

  //#region relatorios
  controleFiscal = httpsCallable<{ data: string }, string>(
    getFunctions(),
    "relatorios-controleFiscal"
  );
  declaracao = httpsCallable<{ data: string }, string>(
    getFunctions(),
    "relatorios-declaracao"
  );
  // Apuração Internacional do mês (PDF): recebe a data-base do mês, devolve a
  // URL assinada do arquivo.
  relatorioInternacional = httpsCallable<{ data: string }, string>(
    getFunctions(),
    "relatorios-relatorioInternacional"
  );
  //#endregion

  //#region ia
  analiseInteligenteMensal = httpsCallable<{ data: string }, void>(
    getFunctions(),
    "ia-analiseInteligenteMensal"
  );

  assistenteInternacional = httpsCallable<{ pergunta: string }, string>(
    getFunctions(),
    "ia-assistenteInternacional"
  );
  //#endregion

  //#region usuarios
  usuarioCriado = httpsCallable<
    {
      business?: {
        nome: string;
        cnpj: string;
      };
    },
    void
  >(getFunctions(), "usuario-usuarioCriado");

  testarInteligencia = httpsCallable<void, void>(
    getFunctions(),
    "usuario-testarInteligencia"
  );
  //#endregion

  //#region conexoes
  connectToken = httpsCallable<
    { conexaoId: string; itemId?: string },
    { connectToken: string }
  >(getFunctions(), "conexoes-connectToken");

  criarLinkConexao = httpsCallable<
    { conexaoId: string },
    { linkConexao: string }
  >(getFunctions(), "conexoes-criarLinkConexao");

  //#endregion
}
